import { useEffect } from 'react'
import { ApolloClient } from '@apollo/client'
import { setCookie } from 'cookies-next'
import { GetServerSideProps } from 'next'
import { ParsedUrlQuery } from 'querystring'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { AppFeatures } from '@/experimentation'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { useLegacyEffect } from '@/molecules/utils'
import { getServerSideClient } from '@/services/ApolloClient'
import { CatalogTitle, fetchTitle } from '@/services/ContentCatalog'
import { ValidSale } from '@/services/ContentfulSaleService'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import { GetGuildTheatricalReleasesResult } from '@/services/GuildReleasesService/TheatricalReleaseService'
import {
  getGuildSignupPageBySlug,
  GuildEarlyAccessSectionModel,
  GuildSignupPageData,
} from '@/services/GuildSignupPageService'
import {
  GuildProjectSlugs,
  GuildSignupEmphasis,
  GuildSignupLayout,
} from '@/services/GuildSignupPageService/GuildSignupPageTypes'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { GuildSale, transformContentfulSaleToValidatedSale } from '@/services/SaleService'
import { GuildSignupExplainerLayout, GuildTestimonialSection } from '@/types/codegen-contentful'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { projectSlugsAttribution } from '@/utils/analytics/attribution'
import { getServerSidePropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { omitUndefineds } from '@/utils/object'
import { writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { getUserGuildStatus, getUserUuidFromJwt, GuildStatus } from '@/utils/users/usersServer'
import { GuildMovieTicketRelease } from '@/views/GuildDashboardView/GuildMovieTicketsPanel/types'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'
import { getGuildApprovedTorchPosters } from '@/views/GuildSignupView/GuildSignupContext/GuildSignupContext'
import { MarketingWinbackView } from '@/views/MarketingWinbackView'

interface GuildSignupProps {
  catalogTitle?: CatalogTitle
  emphasis: GuildSignupEmphasis
  guildMemberCta: string
  guildMemberHref: string
  guildApprovedTorchPosters: string[]
  guildExplainerLayout?: GuildSignupExplainerLayout | null
  layout: GuildSignupLayout
  locale: string
  projectSlugs?: GuildProjectSlugs
  promotion: Promotion | null
  sale: GuildSale | null
  slug: string
  theatricalReleases: GetGuildTheatricalReleasesResult
  votingPhoneImageUrl?: string | null
  guildTestimonialSection?: GuildTestimonialSection
  guildEarlyAccessSection?: GuildEarlyAccessSectionModel
  movieTicketReleases: GuildMovieTicketRelease[]
  hasUser: boolean
  logo?: {
    src: string
    alt: string
    className?: string
  }
  experiments: Record<keyof AppFeatures, unknown>
}

export const GuildSignupPage: NextPageWithLayout<GuildSignupProps> = ({
  catalogTitle,
  emphasis,
  guildMemberCta,
  guildMemberHref,
  guildApprovedTorchPosters,
  guildExplainerLayout,
  layout,
  locale,
  projectSlugs,
  promotion,
  sale,
  slug,
  theatricalReleases,
  votingPhoneImageUrl,
  guildTestimonialSection,
  guildEarlyAccessSection,
  movieTicketReleases,
  logo,
  experiments,
}) => {
  const { t } = useTranslate('guild')
  const track = useSafeTrack()

  useLegacyEffect(() => {
    track('Guild Product Page Viewed', { pageContext: emphasis, slug })
    setCookie('guildFocus', slug, { maxAge: Durations.ONE_YEAR_IN_SECONDS })
  }, [track, slug, emphasis])

  useEffect(() => {
    writeToSessionStorage('pageSlug', slug)
    projectSlugsAttribution(projectSlugs)
  }, [slug, projectSlugs])

  useStickyAttribution('guild-attribution')

  return (
    <div className={isGuildSignupSimpleLayout(layout) ? 'bg-white' : 'bg-core-gray-950'}>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1717006540/open-graph-240228.jpg',
          cloudinaryTransformation: 'b_rgb:000000',
          description: t(
            'angelStudiosGuildJoinInfoMetaDescriptionV2',
            'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.',
          ),
          locale,
          path: `${paths.guild.join}/${slug}`,
          title: t('angelStudiosGuildEarlyAccessMetaTitleV2', 'Angel Guild Membership | Angel Studios'),
        })}
      />
      <GuildSignupContextProvider
        catalogTitle={catalogTitle}
        guildTestimonialSection={guildTestimonialSection}
        guildEarlyAccessSection={guildEarlyAccessSection}
        emphasis={emphasis}
        guildApprovedTorchPosters={guildApprovedTorchPosters}
        guildExplainerLayout={guildExplainerLayout}
        guildMemberCta={guildMemberCta}
        guildMemberHref={guildMemberHref}
        layout={layout}
        projectSlugs={projectSlugs}
        promotion={promotion}
        sale={sale}
        slug={slug}
        theatricalReleases={theatricalReleases}
        votingPhoneImageUrl={votingPhoneImageUrl}
        movieTicketReleases={movieTicketReleases}
        logo={logo}
        experiments={experiments}
      >
        <MarketingWinbackView />
      </GuildSignupContextProvider>
    </div>
  )
}

function shouldRedirectToDashboard(guildStatus: GuildStatus | null, emphasis: string) {
  if (!guildStatus) return false
  return (
    guildStatus &&
    ((emphasis === 'tickets' && guildStatus.guildRoles?.hasFreeTickets) ||
      (emphasis === 'early-access' && guildStatus.guildRoles?.hasEarlyAccess))
  )
}

function getDestinationUrl(pageData: GuildSignupPageData, emphasis: GuildSignupEmphasis, query: ParsedUrlQuery) {
  let destination = pageData.guildMemberHref ?? `/guild#${emphasis}`
  if (query['tv-post-auth'] === 'True') {
    const hasQuery = destination.includes('?')
    destination = `${destination}${hasQuery ? '&' : '?'}tv-post-auth=True`
  }
  return destination
}

export const getServerSideProps: GetServerSideProps = async ({ req, res, preview = false, params, query }) =>
  getServerSidePropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const slug = 'angel'
    const locale = getLocaleFromParams(params)
    const pageData = await getGuildSignupPageBySlug({ slug, opts: { locale, preview } })
    const client = getServerSideClient({ req, res })

    if (!pageData) {
      return {
        redirect: {
          destination: paths.guild.join,
          permanent: false,
        },
      }
    }

    if (pageData.redirectTo) {
      return {
        redirect: {
          destination: pageData.redirectTo,
          permanent: false,
        },
      }
    }

    const uuid = getUserUuidFromJwt(req, res) ?? null
    const guildStatus = !uuid ? null : await getUserGuildStatus(uuid)
    const emphasis = 'early-access'

    if (shouldRedirectToDashboard(guildStatus, emphasis)) {
      return {
        redirect: {
          destination: getDestinationUrl(pageData, emphasis, query),
          permanent: false,
        },
      }
    }

    const guildApprovedTorchPosters = await getGuildApprovedTorchPosters()

    const isSupportLink = query.special === 'last-chance'
    const promotion = await getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient: client })
    const sale = await getOffer(client)

    const guildExplainerLayout = {
      ...getExplainerLayout(pageData),
      title: 'Continue this incredible journey with us.',
    }

    const catalogTitle = await fetchTitle<CatalogTitle>(locale, pageData?.project?.contentCatalogId)

    const layout = {
      ...pageData.layout,
      heading: 'Your Exclusive Offer is Here',
      paragraph: {
        json: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value:
                    'Stream all of our shows and help us deliver more inspiring stories to the world. Lock in your membership for as low as $1.99/month for a full 6 months.',
                  nodeType: 'text',
                },
              ],
              nodeType: 'paragraph',
            },
          ],
          nodeType: 'document',
        },
      },
    } as GuildSignupLayout

    const experiments = {} as Record<keyof AppFeatures, unknown>

    return {
      props: {
        catalogTitle: omitUndefineds(catalogTitle),
        emphasis,
        guildApprovedTorchPosters,
        guildExplainerLayout,
        guildMemberCta: pageData.guildMemberCta,
        guildMemberHref: pageData.guildMemberHref,
        layout,
        locale,
        projectSlugs: pageData.projectSlugs,
        promotion,
        sale,
        slug,
        votingPhoneImageUrl: pageData.votingPhoneImageUrl ?? null,
        logo: pageData?.project?.logo ?? null,
        hasUser: Boolean(uuid),
        experiments,
        isDarkMode: layout?.__typename !== 'GuildSignupSimpleLayout',
        ...(await loadTranslations(locale, ['common', 'join-the-guild', 'guild', 'guild-benefits', 'app-promo'])),
      },
    }
  })

GuildSignupPage.getLayout = (page, pageProps) => {
  return <DefaultLayout isDarkMode={Boolean(pageProps?.isDarkMode)}>{page}</DefaultLayout>
}

export default GuildSignupPage

function getExplainerLayout(pageData: GuildSignupPageData) {
  return pageData.explainerLayout ?? null
}

async function getOffer(federationClient: ApolloClient<object>) {
  const thing = {
    id: 'winter24',
    category: 'guild',
    promotionsCollection: {
      items: [
        {
          name: 'Special Offer',
          bimId: '1873',
          promoCode: 'BWNTR24',
        },
        {
          name: 'Special Offer',
          bimId: '1848',
          promoCode: 'PWNTR24',
        },
      ],
    },
    name: 'Special Offer',
    abbreviatedPitch: '',
    pitch: '',
  } as ValidSale

  return await transformContentfulSaleToValidatedSale(thing, federationClient)
}
